import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Tag, Button, Space, Tooltip, Empty, Spin, Pagination,
  Dropdown, Modal, message
} from 'antd';
import {
  EditOutlined, EyeOutlined, DeleteOutlined, CopyOutlined,
  LineChartOutlined, HistoryOutlined, MoreOutlined,
  CheckCircleOutlined, CloseCircleOutlined, StarOutlined, StarFilled
} from '@ant-design/icons';
import styled from 'styled-components';
import templateService from 'services/templateService';

const { Meta } = Card;

/**
 * GalleryView - Visual card-based template browsing
 * Features:
 * - Large template cards with thumbnails
 * - Visual indicators (active, default, starred)
 * - Quick actions on hover
 * - Pagination
 * - Responsive grid layout
 */
const GalleryView = ({ filters, onEdit, onPreview, onViewStats, onViewHistory, onRefresh }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [starred, setStarred] = useState(new Set());

  const loadTemplates = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await templateService.getTemplates({
        ...filters,
        page,
        pageSize,
      });

      if (response.success) {
        setTemplates(response.data.templates || response.data || []);
        setTotal(response.data.total || response.data.length || 0);
      }
    } catch (error) {
      console.error('Gallery view error:', error);
      message.error('Failed to load templates. Using offline mode.');
      // Set empty array to show empty state
      setTemplates([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDelete = async (template) => {
    Modal.confirm({
      title: 'Delete Template',
      content: `Are you sure you want to delete "${template.templateName}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await templateService.deleteTemplate(template.templateId);
          message.success('Template deleted successfully');
          onRefresh?.();
          loadTemplates();
        } catch (error) {
          message.error('Failed to delete template: ' + error.message);
        }
      },
    });
  };

  const handleClone = async (template) => {
    try {
      const clonedTemplate = {
        ...template,
        templateName: `${template.templateName} (Copy)`,
        isDefault: false,
      };
      delete clonedTemplate.templateId;

      await templateService.createTemplate(clonedTemplate);
      message.success('Template cloned successfully');
      onRefresh?.();
      loadTemplates();
    } catch (error) {
      message.error('Failed to clone template: ' + error.message);
    }
  };

  const toggleStar = (templateId) => {
    setStarred(prev => {
      const newStarred = new Set(prev);
      if (newStarred.has(templateId)) {
        newStarred.delete(templateId);
      } else {
        newStarred.add(templateId);
      }
      return newStarred;
    });
  };

  const getMenuItems = (template) => [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => onEdit?.(template),
    },
    {
      key: 'preview',
      label: 'Preview',
      icon: <EyeOutlined />,
      onClick: () => onPreview?.(template),
    },
    {
      key: 'clone',
      label: 'Clone',
      icon: <CopyOutlined />,
      onClick: () => handleClone(template),
    },
    {
      key: 'stats',
      label: 'View Stats',
      icon: <LineChartOutlined />,
      onClick: () => onViewStats?.(template),
    },
    {
      key: 'history',
      label: 'Version History',
      icon: <HistoryOutlined />,
      onClick: () => onViewHistory?.(template),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDelete(template),
    },
  ];

  const getLevelColor = (level) => {
    const colors = {
      'page': 'blue',
      'module': 'green',
      'dashboard': 'orange',
      'hrst': 'purple',
    };
    return colors[level] || 'default';
  };

  const getScopeIcon = (scope) => {
    const icons = {
      'SYSTEM': '🌐',
      'ORGANIZATION': '🏢',
      'USER': '👤',
    };
    return icons[scope] || '📄';
  };

  // Generate thumbnail preview from HTML template
  const getThumbnail = (template) => {
    // In a real implementation, this would render a miniature version of the HTML template
    // For now, we'll use a placeholder with template info
    return (
      <ThumbnailPlaceholder level={template.analysisLevel}>
        <ThumbnailIcon>{getScopeIcon(template.scope)}</ThumbnailIcon>
        <ThumbnailText>{template.analysisLevel.toUpperCase()}</ThumbnailText>
      </ThumbnailPlaceholder>
    );
  };

  if (loading && templates.length === 0) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Loading templates..." />
      </LoadingContainer>
    );
  }

  if (!loading && templates.length === 0) {
    return (
      <EmptyContainer>
        <Empty
          description="No templates found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </EmptyContainer>
    );
  }

  return (
    <Container>
      <Row gutter={[24, 24]}>
        {templates.map(template => (
          <Col xs={24} sm={12} md={8} lg={6} key={template.templateId}>
            <StyledCard
              hoverable
              cover={getThumbnail(template)}
              actions={[
                <Tooltip title="Edit">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => onEdit?.(template)}
                  />
                </Tooltip>,
                <Tooltip title="Preview">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => onPreview?.(template)}
                  />
                </Tooltip>,
                <Dropdown
                  menu={{ items: getMenuItems(template) }}
                  trigger={['click']}
                >
                  <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>,
              ]}
            >
              <Meta
                title={
                  <CardTitle>
                    <span>{template.templateName}</span>
                    <StarButton
                      type="text"
                      size="small"
                      icon={starred.has(template.templateId) ? <StarFilled /> : <StarOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStar(template.templateId);
                      }}
                      $starred={starred.has(template.templateId)}
                    />
                  </CardTitle>
                }
                description={
                  <CardDescription>
                    <DescriptionText>
                      {template.description || 'No description'}
                    </DescriptionText>
                    <TagContainer>
                      <Tag color={getLevelColor(template.analysisLevel)}>
                        {template.analysisLevel}
                      </Tag>
                      {template.isActive ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                          Active
                        </Tag>
                      ) : (
                        <Tag icon={<CloseCircleOutlined />} color="default">
                          Inactive
                        </Tag>
                      )}
                      {template.isDefault && (
                        <Tag color="gold">Default</Tag>
                      )}
                    </TagContainer>
                    {template.category && (
                      <CategoryTag>#{template.category}</CategoryTag>
                    )}
                    <MetaInfo>
                      <MetaItem>
                        Version {template.version || 1}
                      </MetaItem>
                      {template.createdAt && (
                        <MetaItem>
                          {new Date(template.createdAt).toLocaleDateString()}
                        </MetaItem>
                      )}
                    </MetaInfo>
                  </CardDescription>
                }
              />
            </StyledCard>
          </Col>
        ))}
      </Row>

      {total > pageSize && (
        <PaginationContainer>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(newPage, newPageSize) => {
              setPage(newPage);
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize);
              }
            }}
            showSizeChanger
            showTotal={(total) => `Total ${total} templates`}
            pageSizeOptions={['12', '24', '48', '96']}
          />
        </PaginationContainer>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 24px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: white;
  border-radius: 8px;
`;

const StyledCard = styled(Card)`
  height: 100%;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  .ant-card-cover {
    padding: 16px;
    background: #f5f5f5;
  }

  .ant-card-actions {
    background: #fafafa;
  }
`;

const ThumbnailPlaceholder = styled.div`
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${props => {
    const colors = {
      'page': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'module': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'dashboard': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'hrst': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    };
    return colors[props.level] || 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
  }};
  border-radius: 8px;
`;

const ThumbnailIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const ThumbnailText = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const CardTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StarButton = styled(Button)`
  color: ${props => props.$starred ? '#faad14' : '#d9d9d9'};
  &:hover {
    color: #faad14 !important;
  }
`;

const CardDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionText = styled.div`
  color: #595959;
  font-size: 13px;
  line-height: 1.4;
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const CategoryTag = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  font-style: italic;
`;

const MetaInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`;

const MetaItem = styled.div`
  color: #8c8c8c;
  font-size: 12px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
  padding: 24px;
  background: white;
  border-radius: 8px;
`;

export default GalleryView;
